import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  locale?: string;
  projectName?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({
  title = "Premium Real Estate Properties in Dubai",
  description = "Discover luxury properties and investment opportunities in Dubai's most prestigious locations. Get personalized property details and expert consultation.",
  image = "https://springfield-re.com/storage/burj-vista/img17.jpg",
  url = typeof window !== 'undefined' ? window.location.href : '',
  type = "website",
  siteName = "Dubai Real Estate",
  locale = "en_US",
  projectName
}) => {
  // Create a more comprehensive meta description using title and subtitle
  const metaDescription = description.length > 160 
    ? description.substring(0, 157) + "..." 
    : description;

  // Create a dynamic title
  const pageTitle = projectName 
    ? `${projectName} | ${siteName}` 
    : `${title} | ${siteName}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="robots" content="index, follow" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#000000" />
      
      {/* Favicon Links */}
      <link rel="icon" type="image/x-icon" href="/favicon/favicon.ico" />
      <link rel="icon" type="image/png" sizes="16x16" href="/favicon/favicon-16x16.png" />
      <link rel="icon" type="image/png" sizes="32x32" href="/favicon/favicon-32x32.png" />
      <link rel="apple-touch-icon" sizes="180x180" href="/favicon/apple-touch-icon.png" />
      <link rel="icon" type="image/png" sizes="192x192" href="/favicon/android-chrome-192x192.png" />
      <link rel="icon" type="image/png" sizes="512x512" href="/favicon/android-chrome-512x512.png" />
      <link rel="manifest" href="/favicon/site.webmanifest" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={url} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="author" content={siteName} />
      <meta name="publisher" content={siteName} />
      <meta name="language" content={locale.split('_')[0]} />
      
      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      
      {/* Structured Data for Real Estate */}
      {projectName && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": siteName,
            "description": metaDescription,
            "url": url,
            "image": image,
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "AE",
              "addressLocality": "Dubai"
            },
            "areaServed": "Dubai",
            "serviceType": "Real Estate Sales and Investment"
          })}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead; 